// No changes needed, just verifying consistency.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiUserLine,
  RiAddLine,
  RiFilter2Line,
  RiSearchLine,
  RiShieldUserLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiEyeOffLine,
  RiToggleLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiTimeLine,
  RiRefreshLine
} from 'react-icons/ri';
import {
  getAllUsers,
  deactivateUser,
  activateUser,
  deleteUser,
  USER_ROLES
} from '../../../models/userModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser, isAuthenticated } from '../../../models/authModel';

const UserList = () => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    searchTerm: '',
    showInactive: true,
  });

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    userId: null,
    userName: ''
  });

  const [deleting, setDeleting] = useState(false);

  // Debug authentication
  useEffect(() => {
    const currentUser = getCurrentUser();
    const isAuth = isAuthenticated();
    const token = localStorage.getItem('token');

    console.log('Authentication Debug:', {
      isAuthenticated: isAuth,
      hasToken: !!token,
      currentUser: currentUser,
      userRole: currentUser?.role
    });

    if (!isAuth) {
      setError('Not authenticated. Please log in.');
      setLoading(false);
      return;
    }

    if (currentUser?.role !== 'admin') {
      setError('Access denied. Admin role required.');
      setLoading(false);
      return;
    }
  }, []);

  // Fetch users data with enhanced debugging
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching users with filters:', filters);

      const { role } = filters;
      const apiFilters = {
        includeInactive: true
      };

      if (role) apiFilters.role = role;

      console.log('📋 API Filters:', apiFilters);

      // Check authentication before making request
      const currentUser = getCurrentUser();
      const token = localStorage.getItem('token');

      if (!token || !currentUser) {
        throw new Error('Authentication required');
      }

      console.log('🔑 Making authenticated request...');

      const response = await getAllUsers(apiFilters);

      console.log('✅ API Response:', response);

      if (response && response.users) {
        setUsers(response.users);
        setDebugInfo(`Loaded ${response.users.length} users successfully`);
        console.log('👥 Users loaded:', response.users);
      } else {
        console.warn('⚠️ No users array in response:', response);
        setUsers([]);
        setDebugInfo('API returned no users array');
      }

    } catch (err) {
      console.error('❌ Error fetching users:', err);
      console.error('📋 Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });

      let errorMessage = 'Failed to load users data';

      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Admin role required.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please check if the backend is running.';
      } else if (err.message.includes('Network Error') || err.code === 'ERR_NETWORK') {
        errorMessage = 'Cannot connect to server. Please check if backend is running on port 3000.';
      }

      setError(errorMessage);
      setDebugInfo(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filters.role]);

  // Manual refresh function
  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    fetchUsers();
  };

  // Handle search
  const handleSearch = (e) => {
    setFilters({ ...filters, searchTerm: e.target.value });
  };

  // Toggle show inactive users
  const toggleShowInactive = () => {
    setFilters({ ...filters, showInactive: !filters.showInactive });
  };

  // Filter users based on search term and active status
  const filteredUsers = users.filter(user => {
    const searchTerm = filters.searchTerm.toLowerCase();
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';

    const matchesSearch = (
      `${firstName} ${lastName}`.toLowerCase().includes(searchTerm) ||
      user.email.toLowerCase().includes(searchTerm) ||
      user.username.toLowerCase().includes(searchTerm) ||
      user.role.toLowerCase().includes(searchTerm)
    );

    // Only include inactive users if showInactive is true
    if (!user.is_active && !filters.showInactive) {
      return false;
    }

    return matchesSearch;
  });

  // Get role display name
  const getRoleDisplay = (role) => {
    const roleObj = USER_ROLES.find(r => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-700';
      case 'front_desk':
        return isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-700';
      case 'accountant':
        return isDarkMode ? 'bg-blue-900 bg-opacity-20 text-blue-400' : 'bg-blue-100 text-blue-700';
      case 'sales':
        return isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-700';
      default:
        return isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-600';
    }
  };

  // Handle delete confirmation dialog
  const openDeleteDialog = (userId, userName) => {
    setDeleteDialog({
      isOpen: true,
      userId,
      userName
    });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      userId: null,
      userName: ''
    });
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!deleteDialog.userId) return;

    try {
      setDeleting(true);

      await deleteUser(deleteDialog.userId);

      // Remove the user from the local state
      setUsers(users.filter(user => user.id !== deleteDialog.userId));

      // Close dialog
      closeDeleteDialog();

    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle user active status
  const toggleUserActiveStatus = async (userId, isCurrentlyActive) => {
    try {
      if (isCurrentlyActive) {
        await deactivateUser(userId);
      } else {
        await activateUser(userId);
      }

      // Update the user's status in the local state
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, is_active: !isCurrentlyActive }
          : user
      ));

    } catch (err) {
      console.error('Error toggling user status:', err);
      setError(err.message || `Failed to ${isCurrentlyActive ? 'deactivate' : 'activate'} user`);
    }
  };

  // Count active and inactive users
  const activeUsersCount = users.filter(user => user.is_active).length;
  const inactiveUsersCount = users.length - activeUsersCount;

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };

  return (
    <div className="space-y-4">
      <div className={`${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-xl border shadow-md overflow-hidden`}>

        {/* Delete Confirmation Dialog */}
        {deleteDialog.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg max-w-md w-full p-6`}>
              <div className="flex items-center mb-4 text-red-500">
                <RiAlertLine size={24} className="me-2" />
                <h3 className="text-lg font-semibold">{t('confirm_deletion') || 'Confirm Deletion'}</h3>
              </div>

              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                {t('delete_user_confirmation', { name: deleteDialog.userName }) || `Are you sure you want to delete user "${deleteDialog.userName}"? This action cannot be undone.`}
              </p>

              <div className="flex justify-end space-s-3">
                <button
                  onClick={closeDeleteDialog}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-zinc-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  disabled={deleting}
                >
                  {t('cancel') || 'Cancel'}
                </button>

                <button
                  onClick={handleDeleteUser}
                  className={`px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center ${deleting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin me-2"></div>
                      {t('deleting') || 'Deleting...'}
                    </>
                  ) : (
                    <>
                      <RiDeleteBin6Line className="me-2" />
                      {t('delete') || 'Delete'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className={`p-4 ${isDarkMode ? 'border-b border-zinc-800' : 'border-b border-gray-200'}`}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiShieldUserLine className="me-2 text-red-500" />
                {t('system_users') || 'System Users'}
              </h2>

              <button
                onClick={handleRefresh}
                className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } transition-colors duration-200`}
                title="Refresh users"
              >
                <RiRefreshLine size={18} />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="relative w-full sm:w-64">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-zinc-400`}>
                  <RiSearchLine size={18} />
                </div>
                <input
                  type="search"
                  className={`block w-full p-2 ${isRTL ? 'pr-10' : 'pl-10'} text-sm ${isDarkMode
                    ? 'bg-zinc-800 border-zinc-700 text-white'
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                    } border rounded-lg focus:ring-red-500 focus:border-red-500`}
                  placeholder={t('search_users') || 'Search users...'}
                  value={filters.searchTerm}
                  onChange={handleSearch}
                />
              </div>

              <button
                onClick={() => setFilterOpen(!filterOpen)}
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                  } border`}
              >
                <RiFilter2Line className="me-2" />
                {t('filter') || 'Filter'}
              </button>

              <button
                onClick={toggleShowInactive}
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${!filters.showInactive
                  ? (isDarkMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-zinc-500 text-white hover:bg-red-600')
                  : (isDarkMode
                    ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300')
                  } ${filters.showInactive ? 'border' : ''}`}
                title={filters.showInactive ? t('hide_inactive_users') : t('show_inactive_users')}
              >
                {filters.showInactive ? (
                  <>
                    <RiEyeOffLine className="me-2" />
                    {t('hide_inactive') || 'Hide Inactive'}
                  </>
                ) : (
                  <>
                    <RiEyeLine className="me-2" />
                    {t('show_inactive') || 'Show Inactive'}
                  </>
                )}
              </button>

              <Link
                to="/admin/users/add"
                className={`px-3 py-2 text-sm rounded-lg flex items-center ${isDarkMode
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
                  } transition-colors duration-200`}
              >
                <RiAddLine className="mr-2" />
                {t('add_user') || 'Add User'}
              </Link>
            </div>
          </div>

          {/* Filter dropdown */}
          {filterOpen && (
            <div className={`mt-4 p-4 ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('role') || 'Role'}
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                    className={`w-full p-2 text-sm rounded-lg ${isDarkMode
                      ? 'bg-zinc-700 border-zinc-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                      } border focus:ring-red-500 focus:border-red-500`}
                  >
                    <option value="">{t('all_roles') || 'All Roles'}</option>
                    {USER_ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
              <div className="flex items-center">
                <RiUserLine className={`text-2xl me-3 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('total_users') || 'Total Users'}
                  </p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {users.length}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-green-50'}`}>
              <div className="flex items-center">
                <RiCheckLine className={`text-2xl me-3 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('active_users') || 'Active Users'}
                  </p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {activeUsersCount}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
              <div className="flex items-center">
                <RiCloseCircleLine className={`text-2xl me-3 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {t('inactive_users') || 'Inactive Users'}
                  </p>
                  <p className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {inactiveUsersCount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className={`inline-block h-8 w-8 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-500 border-t-transparent'
                }`}></div>
              <p className={`mt-4 text-lg font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('loading_users') || 'Loading users...'}
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200'
              } flex flex-col items-center justify-center text-center`}>
              <RiAlertLine className="text-4xl mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('error_loading_users') || 'Error Loading Users'}</h3>
              <p className="mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'
                  } flex items-center`}
              >
                <RiRefreshLine className="mr-2" />
                {t('try_again') || 'Try Again'}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && users.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <RiUserLine className={`text-6xl mb-4 ${isDarkMode ? 'text-gray-600' : 'text-zinc-400'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('no_users_found') || 'No Users Found'}
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                {t('no_users_message') || 'There are no users in the system yet.'}
              </p>
              <Link
                to="/admin/users/add"
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                  } flex items-center`}
              >
                <RiAddLine className="mr-2" />
                {t('create_first_user') || 'Create First User'}
              </Link>
            </div>
          )}

          {/* Users Table */}
          {!loading && !error && filteredUsers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className={`${isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'}`}>
                    <th className={`px-6 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      {t('user') || 'User'}
                    </th>
                    <th className={`px-6 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      {t('role') || 'Role'}
                    </th>
                    <th className={`px-6 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      {t('status') || 'Status'}
                    </th>
                    <th className={`px-6 py-3 text-start text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      {t('last_login') || 'Last Login'}
                    </th>
                    <th className={`px-6 py-3 text-end text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                      {t('actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-zinc-900' : 'bg-white'} divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-gray-200'}`}>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'} transition-colors duration-200`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`flex-shrink-0 h-10 w-10 rounded-full ${isDarkMode ? 'bg-zinc-800' : 'bg-gray-200'} flex items-center justify-center`}>
                            <RiUserLine className={`text-lg ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                          </div>
                          <div className="ms-4">
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {user.firstName || user.first_name} {user.lastName || user.last_name}
                            </div>
                            <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                              {user.email}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-zinc-400'}`}>
                              @{user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplay(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.is_active
                            ? (isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800')
                            : (isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800')
                            }`}>
                            {user.is_active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
                          </span>
                          <button
                            onClick={() => toggleUserActiveStatus(user.id, user.is_active)}
                            className={`ms-2 p-1 rounded ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                              }`}
                            title={user.is_active ? 'Deactivate user' : 'Activate user'}
                          >
                            <RiToggleLine size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <RiTimeLine className={`mr-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={14} />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                            {formatDate(user.last_login)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                        <div className={`flex items-center justify-end ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                          <Link
                            to={`/admin/users/${user.id}/edit`}
                            className={`p-2 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-800' : 'text-red-600 hover:bg-zinc-50'
                              }`}
                            title="Edit user"
                          >
                            <RiPencilLine size={16} />
                          </Link>
                          <button
                            onClick={() => openDeleteDialog(user.id, `${user.firstName || user.first_name} ${user.lastName || user.last_name}`)}
                            className={`p-2 rounded ${isDarkMode ? 'text-red-400 hover:bg-zinc-800' : 'text-red-600 hover:bg-zinc-50'
                              }`}
                            title="Delete user"
                          >
                            <RiDeleteBin6Line size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Filtered results info */}
          {!loading && !error && users.length > 0 && filteredUsers.length !== users.length && (
            <div className={`mt-4 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'} text-center`}>
              {t('showing_filtered_results', {
                showing: filteredUsers.length,
                total: users.length
              }) || `Showing ${filteredUsers.length} of ${users.length} users`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserList;